import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { CardDef, AnimationDirection } from "./types";

const INTAKE_V2_HISTORY_KIND = "jema-intake-v2" as const;

const isDesktop = () =>
  typeof window !== "undefined" && window.innerWidth >= 768;

const CARD_HASH_ALIASES: Record<string, string> = {
  preferredLanguage: "language-selection",
  fullName: "name",
  contactInfo: "contact",
  procedures: "procedure",
  review: "review",
  verification: "verification",
  success: "thank-you",
};

const toKebabCase = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[_\s]+/g, "-")
    .toLowerCase();

const toStepHash = (cardId: string): string =>
  CARD_HASH_ALIASES[cardId] ?? toKebabCase(cardId);

const buildUrlWithStepHash = (idx: number, cards: CardDef[]): string => {
  if (typeof window === "undefined") return "";
  const card = cards[idx];
  if (!card) return `${window.location.pathname}${window.location.search}`;
  const hash = toStepHash(card.id);
  return `${window.location.pathname}${window.location.search}#${hash}`;
};

interface UseCardFlowOptions {
  cards: CardDef[];
  /** Called whenever the card index changes */
  onCardChange?: (index: number) => void;
  /**
   * Card to open on first render. Defaults to 0 (normal intake).
   * Only read on mount — the OTP continuation flow uses it to resume at the
   * first incomplete card. Later changes are ignored.
   */
  initialIndex?: number;
}

export function useCardFlow({
  cards,
  onCardChange,
  initialIndex = 0,
}: UseCardFlowOptions) {
  const startIndexRef = useRef(
    Math.min(Math.max(initialIndex, 0), Math.max(cards.length - 1, 0)),
  );
  const [currentIndex, setCurrentIndex] = useState(startIndexRef.current);
  const [animDir, setAnimDir] = useState<AnimationDirection>("none");
  const currentIndexRef = useRef(startIndexRef.current);
  const cardsRef = useRef(cards);
  const historySeededRef = useRef(false);

  // Keep refs in sync
  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
    onCardChange?.(currentIndex);
  }, [currentIndex, onCardChange]);

  // ─── Mobile history: seed the first entry ────────────────────────────────
  useEffect(() => {
    if (isDesktop() || historySeededRef.current) return;
    historySeededRef.current = true;
    const prev = window.history.state as Record<string, unknown> | null;
    const base =
      prev && typeof prev === "object" && !Array.isArray(prev) ? { ...prev } : {};
    if (
      (base as { intakeKind?: string }).intakeKind === INTAKE_V2_HISTORY_KIND
    ) {
      return;
    }
    window.history.replaceState(
      {
        ...base,
        intakeCardIndex: startIndexRef.current,
        intakeKind: INTAKE_V2_HISTORY_KIND,
      },
      "",
      buildUrlWithStepHash(startIndexRef.current, cardsRef.current),
    );
  }, []);

  // ─── Mobile history: listen for popstate (back gesture) ──────────────────
  useEffect(() => {
    const handler = () => {
      if (isDesktop()) return;
      const st = window.history.state as {
        intakeKind?: string;
        intakeCardIndex?: number;
      } | null;
      if (
        st?.intakeKind !== INTAKE_V2_HISTORY_KIND ||
        typeof st.intakeCardIndex !== "number"
      )
        return;
      const idx = st.intakeCardIndex;
      const len = cardsRef.current.length;
      if (idx >= 0 && idx < len) {
        setAnimDir("backward");
        setCurrentIndex(idx);
      }
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  // ─── Keyboard: Enter = next, Backspace (on empty) just triggers back ──────
  // (Components handle Enter themselves; this is a fallback at the shell level)
  // Nothing needed here — CardShell handles it.

  // ─── Navigation helpers ───────────────────────────────────────────────────
  const pushHistory = useCallback((idx: number) => {
    if (isDesktop()) return;
    const prev = window.history.state as Record<string, unknown> | null;
    const base =
      prev && typeof prev === "object" && !Array.isArray(prev) ? { ...prev } : {};
    window.history.pushState(
      { ...base, intakeCardIndex: idx, intakeKind: INTAKE_V2_HISTORY_KIND },
      "",
      buildUrlWithStepHash(idx, cardsRef.current),
    );
  }, []);

  useEffect(() => {
    if (!isDesktop()) return;
    const prev = window.history.state as Record<string, unknown> | null;
    const base =
      prev && typeof prev === "object" && !Array.isArray(prev) ? { ...prev } : {};
    window.history.replaceState(base, "", buildUrlWithStepHash(currentIndex, cardsRef.current));
  }, [currentIndex]);

  const goNext = useCallback(() => {
    const next = currentIndexRef.current + 1;
    if (next >= cardsRef.current.length) return;
    setAnimDir("forward");
    pushHistory(next);
    setCurrentIndex(next);
  }, [pushHistory]);

  const goBack = useCallback(() => {
    if (currentIndexRef.current <= 0) return;
    if (!isDesktop()) {
      window.history.back();
      return;
    }
    setAnimDir("backward");
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const skipCard = useCallback(() => {
    const next = currentIndexRef.current + 1;
    if (next >= cardsRef.current.length) return;
    setAnimDir("forward");
    pushHistory(next);
    setCurrentIndex(next);
  }, [pushHistory]);

  const jumpToCard = useCallback(
    (id: string) => {
      const idx = cardsRef.current.findIndex((c) => c.id === id);
      if (idx === -1) return;
      const direction: AnimationDirection =
        idx > currentIndexRef.current ? "forward" : "backward";
      setAnimDir(direction);
      pushHistory(idx);
      setCurrentIndex(idx);
    },
    [pushHistory],
  );

  const clampedIndex = Math.min(currentIndex, Math.max(0, cards.length - 1));
  const currentCard = cards[clampedIndex] ?? null;

  // Progress out of all cards minus the success card
  const trackableCount = Math.max(cards.length - 1, 1);
  const progressPercent = Math.min(
    Math.round((clampedIndex / trackableCount) * 100),
    100,
  );

  return {
    currentIndex: clampedIndex,
    currentCard,
    animDir,
    progressPercent,
    isFirst: clampedIndex === 0,
    isLast: clampedIndex === cards.length - 1,
    goNext,
    goBack,
    skipCard,
    jumpToCard,
    setAnimDir,
  };
}
